import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MoveRight, 
  Menu, 
  X, 
  Home, 
  Building2, 
  Compass, 
  MessageSquareQuote, 
  BookOpen, 
  Mail, 
  Phone, 
  MapPin 
} from 'lucide-react';
import logo from '../assets/logo.png';

const Navbar = ({ isTransparent = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navLinks = [
    { label: 'Blogs', to: '/blogs', icon: BookOpen },
    { label: 'Testimonials', to: '/testimonials', icon: MessageSquareQuote },
    { label: 'Contact', to: '/contact', icon: Mail },
    { label: 'Buy Properties', to: '/properties?tab=residential', icon: Building2 },
    { label: 'Buy Land', to: '/properties?tab=land', icon: Compass },
  ];

  return (
    <>
      <motion.header 
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className={`w-full z-40 px-6 py-5 md:px-12 md:py-8 flex justify-between items-center ${
          isTransparent ? 'absolute top-0' : 'bg-ink-900 relative shadow-sm border-b border-ink-800'
        }`}
      >
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={logo}
              alt="340 Real Estate Logo"
              className="w-10 h-10 md:w-16 md:h-16 object-contain group-hover:scale-105 transition-transform"
            />
            <span className="text-lg md:text-2xl font-bold tracking-tight text-white font-display">
              340 REAL ESTATE
            </span>
          </Link>
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-sm font-medium text-white/80 hover:text-white transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/mls"
            className="px-5 py-2.5 bg-white text-ink-900 text-sm font-semibold rounded-full hover:bg-gray-100 transition-colors flex items-center gap-2 shadow-sm"
          >
            Search MLS <MoveRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Mobile Hamburger Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          className="md:hidden flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/15 transition-all"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </motion.header>

      {/* Mobile Drawer & Overlay */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 md:hidden flex justify-end">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Slide-out Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
              className="relative w-[85%] max-w-[360px] h-full bg-ink-900 text-white p-6 shadow-2xl border-l border-white/10 flex flex-col justify-between overflow-y-auto z-10"
            >
              <div>
                {/* Drawer Top Header */}
                <div className="flex items-center justify-between pb-6 border-b border-white/10 mb-6">
                  <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-2.5">
                    <img src={logo} alt="Logo" className="w-9 h-9 object-contain" />
                    <span className="font-bold text-base tracking-tight font-display">340 Real Estate</span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    aria-label="Close menu"
                    className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Primary Navigation Links */}
                <nav className="flex flex-col gap-1.5 mb-6">
                  <Link
                    to="/"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold hover:bg-white/10 text-white/90 hover:text-white transition-colors"
                  >
                    <Home className="w-4 h-4 text-tropical-400" />
                    <span>Home</span>
                  </Link>

                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold hover:bg-white/10 text-white/90 hover:text-white transition-colors"
                      >
                        <Icon className="w-4 h-4 text-tropical-400" />
                        <span>{link.label}</span>
                      </Link>
                    );
                  })}
                </nav>

                {/* Highlighted MLS Button */}
                <div className="pt-2">
                  <Link
                    to="/mls"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-3.5 px-5 bg-white text-ink-900 text-sm font-bold rounded-xl hover:bg-gray-100 transition-colors shadow-lg"
                  >
                    Search MLS <MoveRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Drawer Bottom Info */}
              <div className="pt-6 border-t border-white/10 mt-6 space-y-3 text-xs text-white/60">
                <a 
                  href="tel:+13406436068" 
                  className="flex items-center gap-2.5 hover:text-tropical-400 transition-colors"
                >
                  <Phone className="w-3.5 h-3.5 text-tropical-400" />
                  <span>+1 340-643-6068</span>
                </a>
                <a 
                  href="mailto:340realestateco@gmail.com" 
                  className="flex items-center gap-2.5 hover:text-tropical-400 transition-colors"
                >
                  <Mail className="w-3.5 h-3.5 text-tropical-400" />
                  <span>340realestateco@gmail.com</span>
                </a>
                <div className="flex items-center gap-2.5">
                  <MapPin className="w-3.5 h-3.5 text-tropical-400" />
                  <span>PO Box 766, St. John, VI 00831</span>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
